import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FaRupeeSign, FaDollarSign, FaExchangeAlt } from 'react-icons/fa';
import './PriceManagement.css';

const PriceManagement = () => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    interviewType: 'DSA',
    price: '',
    currency: 'INR'
  });
  
  // Exchange rate from backend
  const [exchangeRate, setExchangeRate] = useState(83.5);

  useEffect(() => {
    fetchPrices();
  }, []);

  const fetchPrices = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/prices');
      
      if (res.data && res.data.length > 0) {
        setPrices(res.data);
        // Get exchange rate from the first price (they all have the same rate)
        if (res.data[0].exchangeRate) {
          setExchangeRate(parseFloat(res.data[0].exchangeRate));
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching prices:', err);
      toast.error('Failed to fetch prices');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate price
      const price = parseFloat(formData.price);
      if (isNaN(price) || price <= 0) {
        return toast.error('Price must be a positive number');
      }
      
      const res = await api.put('/api/prices', {
        interviewType: formData.interviewType,
        price,
        currency: formData.currency
      });
      
      toast.success(`Price for ${formData.interviewType} interviews updated successfully`);
      fetchPrices();
      
      // Reset form
      setFormData({
        interviewType: 'DSA',
        price: '',
        currency: 'INR'
      });
    } catch (err) {
      console.error('Error updating price:', err);
      toast.error(err.response?.data?.message || 'Failed to update price');
    }
  };

  // Format price with currency symbol
  const renderPrice = (price, currency = 'INR') => {
    // Helper function to safely format currency values
    const formatCurrency = (value) => {
      if (value === undefined || value === null) return '0.00';
      const num = Number(value);
      return isNaN(num) ? '0.00' : num.toFixed(2);
    };

    // Determine the display value
    const displayValue = currency === 'INR' 
      ? price?.price || price 
      : price?.usdPrice || price?.price || price;

    return (
      <div className={`price-display ${currency.toLowerCase()}-price`}>
        {currency === 'INR' ? (
          <FaRupeeSign className="currency-icon" />
        ) : (
          <FaDollarSign className="currency-icon" />
        )}
        {formatCurrency(displayValue)}
      </div>
    );
  
  };

  return (
    <div className="admin-section price-management">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Interview Price Management</h2>
        <div className="exchange-rate-info">
          <FaExchangeAlt className="exchange-icon" />
          <span>1 USD = {exchangeRate.toFixed(2)} INR</span>
        </div>
      </div>
      
      <div className="admin-grid">
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Current Prices</h3>
          </div>
          <div className="admin-card-body">
          {loading ? (
            <p>Loading prices...</p>
          ) : prices.length === 0 ? (
            <p>No prices set yet</p>
          ) : (
            <div className="price-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Interview Type</th>
                    <th>Price (INR)</th>
                    <th>Price (USD)</th>
                    <th>Last Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {prices.map(price => (
                    <tr key={price._id}>
                      <td className="interview-type">{price.interviewType}</td>
                      <td className="inr-price">
                        {renderPrice(price, 'INR')}
                      </td>
                      <td className="usd-price">
                        {renderPrice(price, 'USD')}
                      </td>
                      <td className="updated-at">
                        {new Date(price.updatedAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </div>
        </div>
        
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Update Price</h3>
          </div>
          <div className="admin-card-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="interviewType">Interview Type</label>
              <select
                id="interviewType"
                name="interviewType"
                value={formData.interviewType}
                onChange={handleChange}
                required
              >
                <option value="DSA">DSA</option>
                <option value="System Design">System Design</option>
                <option value="Behavioral/Leadership">Behavioral/Leadership</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="price">Price</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="Enter price"
                min="0"
                step="0.01"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="currency">Currency</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                required
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            
            <button type="submit" className="btn btn-primary">
              Update Price
            </button>
          </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceManagement;
